import PropTypes from 'prop-types';
import React from 'react';
import {Button, Grid} from 'semantic-ui-react';
import {
    get as _get,
    includes as _includes,
    remove as _remove,
} from 'lodash';
import {reduxForm} from 'redux-form';

import Form from '@appComponents/ReduxFormControls';
import {FormWrapper} from '@appComponents/HOCFormWrapper';
import {convertToInt} from '@utils/helpers';
import {
    PROPERTY_LICENCE_TYPE_OTHER,
    PROPERTY_LICENCE_TYPE_STAGE_SPORT,
    PROPERTY_LICENCE_TYPE_SPORTRADAR,
} from '@constants/variables';
import StageCategoryFormColumns from '@modules/propertyLicence/forms/StageCategory/StageCategoryFormColumns';
import stageCategoriesDataPropTypes from '@modules/propertyLicence/utils/stageCategoriesManager';

import validate from './../forms/validatorContent';

export class PropertyLicenceContentForm extends React.Component {
    static propTypes = {
        contentCategoriesSecondLevel: PropTypes.array,
        contentCategoriesThirdLevel: PropTypes.array,
        contentCategoriesThirdLevelLoading: PropTypes.bool,
        contentCategoryThirdLevelValue: PropTypes.string,
        contentTiers: PropTypes.array,
        dataForForm: PropTypes.object,
        formData: PropTypes.object.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        onAdd: PropTypes.func,
        onTournamentCategoryChange: PropTypes.func,
        onTournamentChange: PropTypes.func,
        onContentCategorySecondLevelChange: PropTypes.func,
        onContentCategoryThirdLevelChange: PropTypes.func,
        onDeactivateEditableCell: PropTypes.func,
        onUpdate: PropTypes.func,
        propertyLicence: PropTypes.object,
        propertyLicenceContent: PropTypes.array,
        reset: PropTypes.func,
        staticData: PropTypes.object,
        submitting: PropTypes.bool,
        tournamentCategories: PropTypes.array,
        tournaments: PropTypes.array,
        tournamentsLoading: PropTypes.bool,
        tournamentValue: PropTypes.string,
        columns: PropTypes.object,
        authorization: PropTypes.shape({
            read: PropTypes.bool,
            create: PropTypes.bool,
            update: PropTypes.bool,
            delete: PropTypes.bool,
        }),
        stageCategoriesManager: stageCategoriesDataPropTypes,
    };

    static defaultProps = {
        contentCategoriesSecondLevel: null,
        contentCategoriesThirdLevel: null,
        contentCategoriesThirdLevelLoading: false,
        contentCategoryThirdLevelValue: '',
        contentTiers: [],
        dataForForm: {},
        onAdd: null,
        onTournamentCategoryChange: null,
        onTournamentChange: null,
        onContentCategorySecondLevelChange: null,
        onContentCategoryThirdLevelChange: null,
        onDeactivateEditableCell: null,
        onUpdate: null,
        propertyLicence: {},
        propertyLicenceContent: [],
        reset: null,
        staticData: {},
        submitting: false,
        tournamentCategories: null,
        tournaments: null,
        tournamentsLoading: false,
        tournamentValue: '',
        columns: {},
        authorization: {
            read: false,
            create: false,
            update: false,
            delete: false,
        },
        stageCategoriesManager: null,
    };

    state = {
        contentCategory2: null,
        tournamentCategory: null,
    };

    renderTournamentCategoryColumn = () => {
        if (null === this.props.tournamentCategories && !this.props.onAdd) {
            return _get(this.props, 'staticData.tournament_category.name', '');
        }

        return (
            <Form.FormRow
                component={Form.SemanticSelect}
                onChangeSelect={this.props.onTournamentCategoryChange}
                options={this.props.tournamentCategories || []}
                name='tournament_category'
                placeholder='Select category'
                defaultValue={this.state.tournamentCategory}
                required
            />
        );
    };

    renderTournamentColumn = () => {
        if (null === this.props.tournaments && !this.props.onAdd) {
            return _get(this.props, 'staticData.tournament.name', '');
        }

        const contentTournamentsIds = (this.props.propertyLicenceContent ?? []).map(c => c.tournament.id);

        let tournaments = [];

        if (this.props.tournaments) {
            tournaments = _remove([...this.props.tournaments], t =>
                !_includes(contentTournamentsIds, t.value)
            );
        }

        return (
            <Form.FormRow
                component={Form.SemanticSelect}
                defaultValue={this.props.tournamentValue}
                disabled={!this.props.tournaments}
                loading={this.props.tournamentsLoading}
                name='tournament'
                onChangeSelect={this.props.onTournamentChange}
                options={tournaments}
                placeholder='Select tournament'
                required
            />
        );
    };

    renderSecondLevelCategoryColumn = () => {
        if (null === this.props.contentCategoriesSecondLevel) {
            return _get(this.props, 'staticData.content_category_level_2.name', '');
        }

        return (
            <Form.FormRow
                component={Form.SemanticSelect}
                name='content_category_2'
                onChangeSelect={this.props.onContentCategorySecondLevelChange}
                options={this.props.contentCategoriesSecondLevel}
                placeholder='Select 2nd level category'
                defaultValue={this.state.contentCategory2}
                required
            />
        );
    };

    renderThirdLevelCategoryColumn = () => {
        if (null === this.props.contentCategoriesThirdLevel && !this.props.onAdd) {
            return _get(this.props, 'staticData.content_category_level_3.name', '');
        }

        const currentValue = convertToInt(this.props.contentCategoryThirdLevelValue);
        const usedIds = (this.props.propertyLicenceContent ?? [])
            .map(c => c.content_category.id)
            .filter((id) => id !== currentValue);

        let options = [];

        if (this.props.contentCategoriesThirdLevel) {
            options = _remove([...this.props.contentCategoriesThirdLevel], c =>
                !_includes(usedIds, c.value)
            );
        }

        return (
            <Form.FormRow
                component={Form.SemanticSelect}
                disabled={!this.props.contentCategoriesThirdLevel}
                loading={this.props.contentCategoriesThirdLevelLoading}
                name='content_category_3'
                defaultValue={this.props.contentCategoryThirdLevelValue}
                onChangeSelect={this.props.onContentCategoryThirdLevelChange}
                options={options}
                placeholder='Select 3rd level category'
                required
            />
        );
    };

    renderContentTierDropdown = () => (
        <Form.FormRow
            component={Form.SemanticSelect}
            defaultValue={
                this.props.onUpdate
                    ? this.props.dataForForm.content_tier.id
                    : this.props.propertyLicence.content_tier_id
            }
            name='content_tier'
            options={this.props.contentTiers}
            placeholder='Select tier'
            required
            resetToDefaultValue
        />
    );

    onFormSubmit = (data) => {
        if (this.props.onAdd) {
            this.props.onAdd(data);
            this.props.reset();
            this.setState({
                tournamentCategory: data.tournament_category,
                contentCategory2: data.content_category_2,
            });
        } else {
            this.props.onDeactivateEditableCell();
            this.props.onUpdate(data);
        }
    };

    render() {
        const requiredAuthorization = this.props.onAdd
            ? this.props.authorization.create
            : this.props.authorization.update;

        if (!this.props.authorization.read || !requiredAuthorization) {
            return 'Not authorized';
        }

        let gridColumns;

        switch (convertToInt(this.props.propertyLicence.property_licence_type_id)) {
            case PROPERTY_LICENCE_TYPE_SPORTRADAR:
                gridColumns = (<>
                    <Grid.Column width={this.props.columns.tournament_category.width}>
                        {this.renderTournamentCategoryColumn()}
                    </Grid.Column>
                    <Grid.Column width={this.props.columns.tournament.width}>
                        {this.renderTournamentColumn()}
                    </Grid.Column>
                </>);
                break;
            case PROPERTY_LICENCE_TYPE_STAGE_SPORT:
                gridColumns = <StageCategoryFormColumns
                    columns={this.props.columns}
                    stageCategoriesManager={this.props.stageCategoriesManager}
                    staticData={this.props.staticData}
                />;
                break;
            case PROPERTY_LICENCE_TYPE_OTHER:
                gridColumns = (<>
                    <Grid.Column width={this.props.columns.content_category_level_2.width}>
                        {this.renderSecondLevelCategoryColumn()}
                    </Grid.Column>
                    <Grid.Column width={this.props.columns.content_category_level_3.width}>
                        {this.renderThirdLevelCategoryColumn()}
                    </Grid.Column>
                </>);
                break;
        }

        return (
            <Form.Create
                className='propertyLicenceContentRow'
                onSubmit={this.props.handleSubmit(this.onFormSubmit)}
            >
                <Form.IdField
                    defaultValue={this.props.dataForForm.property_licence_content.id}
                    name='id'
                />

                <Grid columns={4} verticalAlign='middle'>
                    <Grid.Row>
                        {gridColumns}
                        <Grid.Column width={this.props.columns.content_tier.width}>
                            {this.renderContentTierDropdown()}
                        </Grid.Column>
                        <Grid.Column width={this.props.columns.actions.width}>
                            {this.props.onAdd
                                ? (
                                    <Button
                                        icon='plus'
                                        type='submit'
                                    />
                                )
                                : (
                                    <Button.Group>
                                        <Button
                                            icon='checkmark'
                                            type='submit'
                                            positive
                                        />
                                        <Button
                                            icon='cancel'
                                            negative
                                            type='button'
                                            onClick={this.props.onDeactivateEditableCell}
                                        />
                                    </Button.Group>
                                )
                            }
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Form.Create>
        );
    }
}

const PropertyLicenceContentFormWithRedux = reduxForm({
    form: 'PropertyLicenceContent',
    validate,
})(PropertyLicenceContentForm);

export default FormWrapper(
    {
        property_licence_content: {id: ''},
        content_category: {id: ''},
        stage_category: {id: ''},
        tournament: {
            id: '',
            tournament_category: {id: ''},
        },
        content_tier: {id: ''},
    },
    PropertyLicenceContentFormWithRedux
);
